/**
 * useTenantChatConfig — provides tenant-aware chat configuration
 * for GlobalChatSystem instances.
 *
 * Resolves the current user's tenant and returns chat config overrides
 * (welcome message, history key, persona name, etc.)
 *
 * Falls back to defaults if no tenant is found.
 */

import { useState, useEffect } from 'react';
import {
  resolveTenantId,
  getUserTenantMembership,
  invalidateTenantCache,
} from '../services/tenantResolver';
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';
import { getApp } from 'firebase/app';
import {
  GENERIC_WELCOME,
  FERNANDO_WELCOME,
  GENERIC_WELCOME_STARTERS,
  FERNANDO_WELCOME_STARTERS,
  getGuestWelcome,
  getGuestWelcomeStarters,
} from '../data/welcomeCopy';
import { getUserUiLanguage } from '../services/threadMetadata/uiLanguage';
import {
  FERNANDO_ACCOUNT_ID,
  FERNANDO_ATTORNEY_EMAILS,
  isFernandoProfile,
} from '../services/tenantBranding';
import { pickBackPointerAccountId } from '../services/prest0nRuntime/backPointer';
import type { WelcomeSource } from '../services/threadMetadata/greetingRank';

/**
 * Decide which built-in welcome copy to use as the in-code fallback.
 *
 * Resolution order:
 *   1. Fernando tenant detected via accountId, attorney email on the
 *      Firebase auth user, or `isFernandoProfile` on the loaded profile
 *      → FERNANDO_WELCOME (firm-aware copy with practice-area tools).
 *   2. Anything else → GENERIC_WELCOME (neutral bilingual copy).
 *
 * Live Firestore overrides at
 *   `tenants/{tenantId}/config/runtime.chatWelcomeMessage` and
 *   `accounts/{uid}/config/runtime.chatWelcomeMessage`
 * still win over this fallback in `useTenantChatConfig` below.
 */
function pickFallbackWelcome(opts: {
  tenantId: string | null;
  authEmail: string | null;
  profile: Record<string, unknown> | null;
}): string {
  const { tenantId, authEmail, profile } = opts;
  if (tenantId === FERNANDO_ACCOUNT_ID) return FERNANDO_WELCOME;
  if (
    authEmail &&
    FERNANDO_ATTORNEY_EMAILS.some((e) => e.toLowerCase() === authEmail.toLowerCase())
  ) {
    return FERNANDO_WELCOME;
  }
  if (profile && isFernandoProfile(profile)) return FERNANDO_WELCOME;
  return GENERIC_WELCOME;
}

/**
 * Canonical starter prompts for the resolved welcome variant. Mirrors
 * `pickFallbackWelcome`'s Fernando-vs-generic branching so the empty-thread
 * starter CHIPS (F4-A) always match the firm-aware welcome — and, per F4-A
 * recommendation 4a, stay the canonical 4 even when the welcome *message* text
 * is overridden freeform in Firestore. Returns a fresh array (safe to mutate).
 */
function pickFallbackStarters(opts: {
  tenantId: string | null;
  authEmail: string | null;
  profile: Record<string, unknown> | null;
}): string[] {
  const { tenantId, authEmail, profile } = opts;
  const isFernando =
    tenantId === FERNANDO_ACCOUNT_ID ||
    (!!authEmail &&
      FERNANDO_ATTORNEY_EMAILS.some((e) => e.toLowerCase() === authEmail.toLowerCase())) ||
    (!!profile && isFernandoProfile(profile));
  return [...(isFernando ? FERNANDO_WELCOME_STARTERS : GENERIC_WELCOME_STARTERS)];
}

interface TenantChatConfig {
  /** Tenant ID (null if no tenant) */
  tenantId: string | null;
  /** User's role in the tenant */
  role: 'admin' | 'staff' | 'viewer' | null;
  /** Persona name to display */
  personaName: string;
  /** Welcome message text */
  welcomeMessage: string;
  /** Canonical one-click starter prompts for the empty-thread welcome (F4-A). */
  welcomeStarters: string[];
  /** History storage key prefix */
  historyKeyPrefix: string;
  /** Whether config has loaded */
  loaded: boolean;
  /**
   * Where `welcomeMessage` came from (2026-08-09 multi-tab greeting parity):
   * drives the persisted greeting's monotonic rank so a tab holding a WORSE
   * copy (guest/error/generic fallback, legacy tenant override) can never
   * clobber a better persisted one. See services/threadMetadata/greetingRank.
   */
  welcomeSource: WelcomeSource;
  /** Firm name (if tenant) */
  firmName: string | null;
  /**
   * Memory-distilled net-new automation suggestions for the new-thread
   * greeting (refreshGreetingSuggestions writes these to the runtime
   * config docs; account-level wins over tenant-level).
   */
  suggestedAutomations: string[] | undefined;
  /**
   * Token usage the suggestion distillation reported (written next to
   * `suggestedAutomations` on the same runtime doc, camelCase
   * TokenUsageSnapshot pass-through). Same precedence as the suggestions:
   * account-level wins over tenant-level. Feeds the greeting bubble's ⓘ.
   */
  suggestedAutomationsUsage: Record<string, unknown> | undefined;
  /** Allowed workflows (undefined = all allowed) */
  allowedWorkflows: string[] | undefined;
  /** Legal disclaimer */
  disclaimer: string | null;
}

const DEFAULT_CONFIG: TenantChatConfig = {
  tenantId: null,
  role: null,
  // Default persona name for the chat-avatar label. MUST stay capital-P
  // + zero (`Prest0n`) per Miles directive 2026-05-09 — the chat surface
  // never shows the lowercase brand spelling or any legacy mis-spelling
  // with a regular `s-t-o-n` ending.
  personaName: 'Prest0n',
  // Default fallback used before tenant resolution finishes. We start with
  // the generic copy; once we resolve the tenant/profile/email below we may
  // upgrade this to FERNANDO_WELCOME via pickFallbackWelcome().
  welcomeMessage: GENERIC_WELCOME,
  welcomeStarters: [...GENERIC_WELCOME_STARTERS],
  welcomeSource: 'generic',
  historyKeyPrefix: 'prest0_global_chat',
  loaded: false,
  firmName: null,
  suggestedAutomations: undefined,
  suggestedAutomationsUsage: undefined,
  allowedWorkflows: undefined,
  disclaimer: null,
};

/** Plain-object guard for the usage snapshot read off the runtime docs. */
function asUsageObject(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

export function useTenantChatConfig(): TenantChatConfig {
  const [config, setConfig] = useState<TenantChatConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    let cancelled = false;
    // Monotonic sequence so a load kicked off by an OLDER auth state can
    // never clobber the config written for a NEWER one (e.g. a sign-out tick
    // arriving while the member load's Firestore reads are still in flight).
    let loadSeq = 0;
    // Identity (uid, or null for guest) of the last COMPLETED load —
    // NON-BLANKING REFRESH (2026-08-19): same-identity reloads (runtime-doc
    // emissions, suggestion refreshes) keep serving the previous config until
    // the new one lands. Blanking `loaded` on every reload opened windows
    // where the greeting composer yielded NOTHING — a new thread created in
    // that window seeded no welcome and no starter chips (owner report:
    // blank new threads in some organizations).
    let lastLoadedIdentity: string | null | undefined = undefined;

    const loadConfig = async (currentUser: User | null) => {
      const seq = ++loadSeq;
      const stale = () => cancelled || seq !== loadSeq;
      try {
        const db = getFirestore(getApp());

        // ── L2 guest gate (2026-07-15) — BEFORE tenant resolution ──────────
        // EVERY signed-out surface gets the qualifying guest welcome +
        // service-question starters (G1 gate on !currentUser, adversarial
        // review Finding 5 kept: returning signed-out devices included).
        // Moved ahead of resolveTenantId because its URL fallback (e.g.
        // /justiceisurright) would otherwise route a SIGNED-OUT visitor into
        // the tenant/member copy with work-action starters the guest backend
        // must decline. Language resolves through the app's canonical UI-
        // language source (EN/ES). Signed-in resolution below is untouched.
        // F2 (adversarial review 2026-07-15): `currentUser` here is the
        // SETTLED auth state delivered by the subscription below — never a
        // synchronous pre-hydration auth.currentUser read.
        if (!currentUser) {
          if (stale()) return;
          const guestLanguage = getUserUiLanguage();
          lastLoadedIdentity = null;
          setConfig({
            ...DEFAULT_CONFIG,
            loaded: true,
            welcomeMessage: getGuestWelcome(guestLanguage),
            welcomeStarters: getGuestWelcomeStarters(guestLanguage),
            welcomeSource: 'guest',
          });
          return;
        }

        // ── IDENTITY-TRANSITION GUARD (2026-08-03) ────────────────────────
        // A signed-out visitor's config holds the GUEST welcome with
        // loaded:true. On sign-in this async member resolution takes a moment,
        // and during that window the greeting composer would still read the
        // guest copy — which is exactly how a brand-new trial ended up greeted
        // by the generic marketing message instead of its personalized welcome
        // (owner report 2026-08-02). Dropping `loaded` for the transition makes
        // App.tsx stop merging the previous identity's welcome/starters, so the
        // composer yields nothing and the seeder's documented
        // "composer yielding nothing → return without caching, retry on
        // composer change" path holds the greeting until the real one arrives.
        // NON-BLANKING REFRESH (2026-08-19): blank ONLY on an actual identity
        // change. Same-identity reloads (live runtime-doc emissions) keep the
        // previous config live — a lost race or slow read must never leave a
        // signed-in session stuck at loaded:false serving no greeting.
        if (lastLoadedIdentity !== currentUser.uid) {
          setConfig((prev) => (prev.loaded ? { ...prev, loaded: false } : prev));
        }

        // Account config — keyed to the ACTIVE WORKSPACE, not the signed-in
        // uid (multi-org E37, 2026-08-16): welcome + starters + suggested
        // automations are ORGANIZATION chrome, so switching organizations must
        // switch them. The back-pointer is the same server-written pointer
        // every resolver trusts; uid is the fallback (single-org accounts are
        // byte-identical to the old behavior since accountId === uid).
        let accountWelcomeMessage: string | null = null;
        let accountWelcomeStarters: string[] | null = null;
        let accountSuggestedAutomations: string[] | undefined;
        let accountSuggestedAutomationsUsage: Record<string, unknown> | undefined;
        let accountProfile: Record<string, unknown> | null = null;
        if (currentUser) {
          let workspaceAccountId = currentUser.uid;
          try {
            const backPointer = await getDoc(doc(db, `users/${currentUser.uid}/account_membership/main`));
            // TOLERANT back-pointer shapes (2026-08-19, owner report: blank
            // new threads in some organizations): live production members
            // still carry the legacy invite shape (defaultAccountId /
            // memberships[] — no top-level accountId). The narrow read here
            // silently fell back to uid, loaded an EMPTY org config, and the
            // thread opened with no greeting and no chips. Use the same
            // tolerant resolver RBAC and trial status already trust.
            const pointed = pickBackPointerAccountId(
              backPointer.data() as Record<string, unknown> | undefined,
            );
            if (pointed) workspaceAccountId = pointed;
          } catch (err) {
            console.warn('[useTenantChatConfig] back-pointer read failed — uid-keyed config fallback', err);
          }
          try {
            const accountConfigSnap = await getDoc(doc(db, `accounts/${workspaceAccountId}/config/runtime`));
            if (accountConfigSnap.exists()) {
              const data = accountConfigSnap.data() || {};
              accountWelcomeMessage = data.chatWelcomeMessage || null;
              // Perfect initial conditions (2026-08-04): onboarding writes the
              // user's tailored starter prompts next to the welcome; they take
              // top precedence over every in-code starter set so the first
              // chips the user sees match their firm profile + answers.
              if (Array.isArray(data.welcomeStarters)) {
                // Up to six (2026-08-04): the bubble quotes the first four,
                // the chip row shows the full set.
                const cleaned = data.welcomeStarters.filter(
                  (x: unknown): x is string => typeof x === 'string' && x.trim().length > 0,
                ).slice(0, 6);
                if (cleaned.length > 0) accountWelcomeStarters = cleaned;
              }
              if (Array.isArray(data.suggestedAutomations)) {
                accountSuggestedAutomations = data.suggestedAutomations.filter(
                  (x: unknown): x is string => typeof x === 'string' && x.trim().length > 0,
                );
              }
              accountSuggestedAutomationsUsage = asUsageObject(data.suggestedAutomationsUsage);
            }
          } catch (err) {
            console.warn('[useTenantChatConfig] account runtime config read failed (non-fatal)', { workspaceAccountId, err });
          }
          // Best-effort profile read for Fernando-by-email detection. Errors
          // are non-fatal — we just fall through to GENERIC_WELCOME. Same
          // workspace keying as the config read above (E37).
          try {
            const onboardingProfileSnap = await getDoc(
              doc(db, `accounts/${workspaceAccountId}/onboarding/profile`),
            );
            if (onboardingProfileSnap.exists()) {
              accountProfile = onboardingProfileSnap.data() as Record<string, unknown>;
            }
          } catch (err) {
            console.warn('[useTenantChatConfig] onboarding profile read failed (non-fatal)', { workspaceAccountId, err });
          }
        }

        const authEmail = currentUser?.email || null;

        const tenantId = await resolveTenantId();
        if (stale()) return;
        if (!tenantId) {
          // Signed-in user without a tenant (the guest gate above already
          // returned for every signed-out surface — G1/L2).
          const fallbackWelcome = pickFallbackWelcome({
            tenantId: null,
            authEmail,
            profile: accountProfile,
          });
          lastLoadedIdentity = currentUser.uid;
          setConfig({
            ...DEFAULT_CONFIG,
            loaded: true,
            welcomeMessage: accountWelcomeMessage || fallbackWelcome,
            welcomeStarters:
              accountWelcomeStarters ||
              pickFallbackStarters({ tenantId: null, authEmail, profile: accountProfile }),
            welcomeSource: accountWelcomeMessage
              ? 'account'
              : fallbackWelcome === GENERIC_WELCOME
                ? 'generic'
                : 'firm',
            suggestedAutomations: accountSuggestedAutomations,
            suggestedAutomationsUsage: accountSuggestedAutomationsUsage,
          });
          return;
        }

        const membership = await getUserTenantMembership();

        const [profileSnap, configSnap] = await Promise.all([
          getDoc(doc(db, `tenants/${tenantId}/profile/main`)),
          getDoc(doc(db, `tenants/${tenantId}/config/runtime`)),
        ]);

        if (stale()) return;

        const profile = profileSnap.data() || {};
        const runtimeConfig = configSnap.data() || {};

        const fallbackWelcome = pickFallbackWelcome({
          tenantId,
          authEmail,
          profile: { ...accountProfile, ...profile },
        });

        lastLoadedIdentity = currentUser.uid;
        setConfig({
          tenantId,
          role: membership?.role || null,
          personaName: runtimeConfig.personaNameOverride || 'Prest0n',
          // Override priority:
          //   1. account-level (onboarding-generated)
          //   2. tenant-level Firestore override
          //   3. firm-aware in-code fallback (Fernando vs. generic)
          welcomeMessage:
            accountWelcomeMessage ||
            runtimeConfig.chatWelcomeMessage ||
            fallbackWelcome,
          welcomeStarters:
            accountWelcomeStarters ||
            pickFallbackStarters({
              tenantId,
              authEmail,
              profile: { ...accountProfile, ...profile },
            }),
          welcomeSource: accountWelcomeMessage
            ? 'account'
            : runtimeConfig.chatWelcomeMessage
              ? 'tenant'
              : fallbackWelcome === GENERIC_WELCOME
                ? 'generic'
                : 'firm',
          historyKeyPrefix: `prest0n_${tenantId}_chat`,
          loaded: true,
          firmName: profile.firmName || null,
          suggestedAutomations:
            accountSuggestedAutomations
            || (Array.isArray(runtimeConfig.suggestedAutomations)
              ? runtimeConfig.suggestedAutomations.filter(
                  (x: unknown): x is string => typeof x === 'string' && x.trim().length > 0,
                )
              : undefined),
          suggestedAutomationsUsage:
            accountSuggestedAutomationsUsage
            || asUsageObject(runtimeConfig.suggestedAutomationsUsage),
          allowedWorkflows: runtimeConfig.allowedWorkflows || undefined,
          disclaimer: runtimeConfig.disclaimers || null,
        });
      } catch (err) {
        console.warn('[useTenantChatConfig] Failed to load tenant config:', err);
        if (!stale()) {
          // L2 fail-safe: even on a config-load error, a SIGNED-OUT visitor
          // must get the qualifying guest greeting — never the member copy
          // with work-action starters ("Draft an NDA…") the guest backend
          // must decline. `currentUser` is this run's SETTLED auth state
          // (F2) — unknown/absent → guest copy (fail-closed toward the
          // guest surface).
          if (!currentUser) {
            const guestLanguage = getUserUiLanguage();
            lastLoadedIdentity = null;
            setConfig({
              ...DEFAULT_CONFIG,
              loaded: true,
              welcomeMessage: getGuestWelcome(guestLanguage),
              welcomeStarters: getGuestWelcomeStarters(guestLanguage),
              welcomeSource: 'guest',
            });
          } else {
            // NON-BLANKING (2026-08-19): a failed RELOAD for the identity we
            // already serve keeps the good config — never downgrade a live
            // personalized greeting to the error fallback.
            if (lastLoadedIdentity === currentUser.uid) return;
            lastLoadedIdentity = currentUser.uid;
            setConfig({ ...DEFAULT_CONFIG, loaded: true, welcomeSource: 'error' });
          }
        }
      }
    };

    // ── F2 (adversarial review 2026-07-15): auth-hydration race fix ────────
    // The old []-deps effect read auth.currentUser SYNCHRONOUSLY once: on a
    // hard reload the Firebase SDK hasn't restored the session yet, so a
    // signed-in member evaluated as a guest and the guest welcome/chips were
    // FROZEN (no re-run on auth transitions; invalidateTenantCache had zero
    // callers). Now the guest-vs-member decision waits for auth hydration and
    // re-runs on every transition:
    //   - firebase 11.10.0 (verified in node_modules) exports
    //     auth.authStateReady(); its resolution point IS the first
    //     onAuthStateChanged emission, and the subscription is needed anyway
    //     for sign-in/sign-out re-runs — so the FIRST tick doubles as the
    //     hydration gate. No decision is made before it: during the
    //     pre-hydration window `config` keeps the pre-G1 DEFAULT_CONFIG
    //     (loaded:false — consumers gate on `loaded`), never guest copy.
    //   - Every subsequent transition invalidates the tenant-membership
    //     cache (it is keyed to the previous user) and reloads the config,
    //     so sign-out flips to guest copy without a reload and sign-in
    //     restores the member/tenant copy.
    // Signed-in resolution above is byte-for-byte the pre-F2 flow.
    const auth = getAuth();
    // `undefined` = no tick observed yet (pre-hydration).
    let lastUid: string | null | undefined = undefined;
    // ── LIVE ACCOUNT-CONFIG (2026-08-04) ──────────────────────────────────
    // The account runtime doc is written AFTER the config's one-shot load on
    // a brand-new trial: redemption seeds the personalized welcome seconds
    // after signup, and onboarding refines it minutes later — but this hook
    // read the doc exactly once per auth transition, so the personalization
    // never reached a session that stayed open (owner repro: fresh Harborview
    // account greeted generically while the suggestion cards — rendered from
    // a different path — were custom). Subscribe to the doc and re-run the
    // full load whenever it changes; loadSeq already serializes overlapping
    // loads, and the greeting reconciliation in useChatThreads picks the new
    // copy up from the composer change.
    let unsubscribeRuntime: (() => void) | null = null;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (cancelled) return;
      const uid = user?.uid ?? null;
      if (lastUid !== undefined && lastUid === uid) return; // duplicate tick, not a transition
      const isTransition = lastUid !== undefined;
      lastUid = uid;
      if (isTransition) invalidateTenantCache();
      if (unsubscribeRuntime) { unsubscribeRuntime(); unsubscribeRuntime = null; }
      void loadConfig(user);
      if (user) {
        try {
          const db = getFirestore(getApp());
          // WORKSPACE-KEYED LIVE CONFIG (2026-08-19, owner report: blank new
          // threads in some organizations): this listener was keyed to the
          // signed-in uid, so for org members (workspaceAccountId ≠ uid) org
          // personalization written after load NEVER reached an open session.
          // Key it off the back-pointer instead: follow the pointer doc and
          // (re)attach the runtime listener to whichever account it resolves
          // to — uid when it points nowhere (single-org accounts unchanged).
          let runtimeDocUnsub: (() => void) | null = null;
          let subscribedAccountId: string | null = null;
          const attachRuntime = (accountId: string) => {
            if (cancelled || subscribedAccountId === accountId) return;
            subscribedAccountId = accountId;
            if (runtimeDocUnsub) { runtimeDocUnsub(); runtimeDocUnsub = null; }
            runtimeDocUnsub = onSnapshot(
              doc(db, `accounts/${accountId}/config/runtime`),
              () => {
                if (cancelled) return;
                // Every emission (including the initial one) re-runs the load:
                // a write landing between loadConfig's getDoc and this
                // subscription attaching must not be missed. Loads are a few
                // doc reads; loadSeq drops stale completions.
                void loadConfig(user);
              },
              (err) => {
                console.warn('[useTenantChatConfig] live runtime-config subscription errored (one-shot load stands)', { accountId, err });
              },
            );
          };
          const backPointerUnsub = onSnapshot(
            doc(db, `users/${user.uid}/account_membership/main`),
            (snap) => {
              if (cancelled) return;
              const pointed = pickBackPointerAccountId(
                snap.data() as Record<string, unknown> | undefined,
              );
              attachRuntime(pointed || user.uid);
            },
            (err) => {
              console.warn('[useTenantChatConfig] back-pointer subscription errored — uid-keyed live config fallback', err);
              attachRuntime(user.uid);
            },
          );
          unsubscribeRuntime = () => {
            backPointerUnsub();
            if (runtimeDocUnsub) { runtimeDocUnsub(); runtimeDocUnsub = null; }
          };
        } catch (err) {
          console.warn('[useTenantChatConfig] live config subscription setup failed (one-shot load stands)', err);
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
      if (unsubscribeRuntime) unsubscribeRuntime();
    };
  }, []);

  return config;
}
